// based on: https://github.com/ianschmitz/react-lazy-with-preload/blob/master/src/index.ts
import { type ComponentType, type ExoticComponent, createElement, lazy, useState } from 'react';

type LazyComponent<P> = ExoticComponent<P> & { preload: () => Promise<ComponentType<P>> };
type Obj = Record<string, unknown>;

export function lazyWithPreload<T extends Obj>(factory: () => Promise<{ default: ComponentType<T> }>): LazyComponent<T>;
export function lazyWithPreload<M extends Obj, K extends keyof M>(
  factory: () => Promise<M>,
  exportName: K
): M[K] extends ComponentType<infer P> ? LazyComponent<P> : never;
export function lazyWithPreload(factory: () => Promise<{ default?: ComponentType<Obj> } & Obj>, exportName?: string) {
  const wrappedFactory = () =>
    factoryPromise ??
    (factoryPromise = factory().then((module) => {
      PreloadedComponent = (exportName ? module[exportName] : module.default) as ComponentType<Obj>;
      return { default: PreloadedComponent };
    }));

  const ReactLazyComponent = lazy(wrappedFactory);
  let PreloadedComponent: ComponentType<Obj> | undefined;
  let factoryPromise: Promise<{ default: ComponentType<Obj> }> | undefined;

  function LazyWithPreload(props: Obj) {
    const [Component] = useState(() => PreloadedComponent ?? ReactLazyComponent);
    return createElement(Component, props);
  }

  LazyWithPreload.preload = () => wrappedFactory().then((m) => m.default);

  return LazyWithPreload as LazyComponent<Obj>;
}
